import React, { useEffect, useState } from "react"
import { useSelector, useDispatch } from "react-redux"
import { useLocation, useNavigate } from "react-router-dom"
import { Spinner } from "reactstrap"
import { fetchCurrentSubscription } from "../../store/subscription/actions"
import { useAuth } from "../../hooks/useAuth"
import { continueWithFreePlan } from "../../api/requests/subscription-requests"
import { isSubscriptionAllowedPath } from "../../constants/subscriptionAllowedRoutes"
import BlurredSkeleton from "./BlurredSkeleton"
import SubscriptionLockedCard, { ADMIN_DECISION_MESSAGE } from "./SubscriptionLockedCard"

// EXPIRED abonelik için kilit kartı içeriği (deneme / ücretli ayrımı)
const buildExpiredContent = (currentStatus) => {
  const planName = currentStatus?.planName
  const isTrial = !planName || planName.toLowerCase() === "deneme"
  return {
    icon: "bx-time-five",
    title: isTrial ? "Deneme Süreniz Doldu" : "Aboneliğiniz Sona Erdi",
    message: isTrial
      ? "Ücretsiz deneme süreniz sona erdi. Sistemi kullanmaya devam etmek için bir abonelik paketi satın almanız gerekmektedir."
      : `"${planName}" aboneliğiniz sona erdi. Ücretsiz paketle devam edebilir ya da yeni bir paket satın alabilirsiniz.`,
    cta: "Abonelik Satın Al",
  }
}

/**
 * Route-seviyesi abonelik kapısı.
 *
 * EXPIRED abonelikli kullanıcı kısıtlı bir route'a girdiğinde gerçek sayfa
 * bileşeni HİÇ mount EDİLMEZ — böylece o sayfanın veri yükleme (useEffect /
 * saga tetikleme) çağrıları çalışmaz, backend'e istek gitmez. Yerine blur'lu
 * dekoratif iskelet + kilit kartı gösterilir.
 *
 * Abonelik ekranları (/subscription/*), profil ve logout her zaman açıktır;
 * kullanıcı paket görüntüleyip satın alabilir / yenileyebilir.
 */
const SubscriptionGate = ({ children }) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const { currentStatus, resolved } = useSelector((state) => state.subscription)
  const { roles } = useAuth()
  const [continuing, setContinuing] = useState(false)
  const [continueError, setContinueError] = useState(null)

  // Kilitli firma Ücretsiz pakete geçer; durum yeniden okununca kilit kalkar ve sayfa açılır.
  const handleContinueFree = async () => {
    setContinuing(true)
    setContinueError(null)
    try {
      await continueWithFreePlan()
      dispatch(fetchCurrentSubscription())
    } catch (e) {
      setContinueError(e?.message || "Ücretsiz pakete geçilemedi")
    } finally {
      setContinuing(false)
    }
  }

  // Route değişiminde abonelik durumunu (yeniden) sorgula — yenileme sonrası
  // ACTIVE'e dönünce kilit otomatik kalkar.
  useEffect(() => {
    dispatch(fetchCurrentSubscription())
  }, [dispatch, location.pathname])

  const isAllowed = isSubscriptionAllowedPath(location.pathname)
  const isSuperAdmin = roles.includes("ROLE_SUPER_ADMIN")
  const isCompanyAdmin = roles.includes("ROLE_COMPANY_ADMIN")

  // Abonelik ekranları / (gerçek) süper admin → koşulsuz normal render.
  // Impersonation'da (süper adminden EXPIRED kullanıcıya geçiş) KASITLI olarak
  // bypass yok: kullanıcının normal girişinde gördüğü blur + kilit ekranının
  // aynısı gösterilir. (Geçişte auth/me impersone edilen kullanıcının rolünü
  // döndürdüğü için isSuperAdmin zaten false olur.)
  if (isAllowed || isSuperAdmin) {
    return children
  }

  // Durum henüz sunucudan sorgulanmadı → sayfayı MOUNT ETME (fetch sızmasın),
  // nötr yükleniyor göstergesi bas. resolved store'da kalıcı olduğundan bu
  // yalnızca ilk korumalı sayfa yüklemesinde görünür.
  if (!resolved) {
    return (
      <div
        className="d-flex flex-column align-items-center justify-content-center"
        style={{ minHeight: "60vh", gap: "1rem" }}
      >
        <Spinner size="lg" color="primary" />
        <span className="text-muted">Abonelik durumu kontrol ediliyor…</span>
      </div>
    )
  }

  // Durum çözüldü ve EXPIRED → sayfayı mount etme, blur + kilit göster
  if (currentStatus?.status === "EXPIRED") {
    return (
      <div style={{ position: "relative", minHeight: "70vh" }}>
        <BlurredSkeleton />
        <div
          style={{
            position: "absolute",
            inset: 0,
            backgroundColor: "rgba(244, 246, 249, 0.35)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            padding: "1rem",
          }}
        >
          {isCompanyAdmin ? (
            <SubscriptionLockedCard
              content={buildExpiredContent(currentStatus)}
              onPrimary={() => navigate("/subscription/plans")}
              onSecondary={() => navigate("/subscription/history")}
              onContinueFree={handleContinueFree}
              continuing={continuing}
              error={continueError}
            />
          ) : (
            <SubscriptionLockedCard
              content={{ ...buildExpiredContent(currentStatus), message: ADMIN_DECISION_MESSAGE }}
            />
          )}
        </div>
      </div>
    )
  }

  // ACTIVE / TRIAL / diğer → normal render
  return children
}

export default SubscriptionGate
